/**
 * 身份驗證存儲庫
 * 提供登入、登出和身份驗證狀態管理功能
 */

import { secureCookies } from "@/infrastructure/storage/secureCookies";
import { COOKIE_KEYS } from "@/domain/constants/cookies";

// 身份驗證相關的所有 Cookie 鍵名
const AUTH_COOKIE_KEYS = [
  COOKIE_KEYS.AUTH_TOKEN,
  COOKIE_KEYS.REFRESH_TOKEN,
  COOKIE_KEYS.USER_ID,
  COOKIE_KEYS.SESSION_ID,
];

// 身份驗證 Token 的預設有效期（小時）
const DEFAULT_TOKEN_EXPIRY_HOURS = 24;

// 類型定義
export type AuthToken = string;
export type RefreshToken = string;
export type UserId = string;
export type SessionId = string;

export interface TokenOptions {
  expiryHours?: number;
  secure?: boolean;
}

export interface RefreshTokenOptions {
  expiryDays?: number;
  secure?: boolean;
}

export interface SessionData {
  authToken: AuthToken;
  refreshToken?: RefreshToken;
  userId: UserId;
  sessionId?: SessionId;
  tokenExpiryHours?: number;
}

/**
 * 身份驗證存儲庫
 * 處理用戶登入、登出和身份驗證狀態管理
 */
export const authRepository = {
  /**
   * 儲存身份驗證 Token
   * @param token 身份驗證 Token
   * @param options Token 選項
   */
  saveToken: (token: AuthToken, options: TokenOptions = {}): void => {
    const { expiryHours = DEFAULT_TOKEN_EXPIRY_HOURS } = options;

    secureCookies.setAuth(COOKIE_KEYS.AUTH_TOKEN, token, {
      expires: expiryHours / 24, // 轉換為天數
    });
  },

  /**
   * 獲取身份驗證 Token
   * @returns 身份驗證 Token 或 null
   */
  retrieveToken: (): AuthToken | null => {
    return secureCookies.get(COOKIE_KEYS.AUTH_TOKEN) as AuthToken | null;
  },

  /**
   * 儲存刷新 Token
   * @param token 刷新 Token
   * @param options Token 選項
   */
  saveRefreshToken: (
    token: RefreshToken,
    options: RefreshTokenOptions = {},
  ): void => {
    const { expiryDays = 30 } = options;

    secureCookies.setAuth(COOKIE_KEYS.REFRESH_TOKEN, token, {
      expires: expiryDays,
    });
  },

  /**
   * 獲取刷新 Token
   * @returns 刷新 Token 或 null
   */
  retrieveRefreshToken: (): RefreshToken | null => {
    return secureCookies.get(COOKIE_KEYS.REFRESH_TOKEN) as RefreshToken | null;
  },

  /**
   * 儲存用戶 ID
   * @param userId 用戶 ID
   */
  saveUserId: (userId: UserId): void => {
    secureCookies.setAuth(COOKIE_KEYS.USER_ID, userId);
  },

  /**
   * 獲取用戶 ID
   * @returns 用戶 ID 或 null
   */
  retrieveUserId: (): UserId | null => {
    return secureCookies.get(COOKIE_KEYS.USER_ID) as UserId | null;
  },

  /**
   * 儲存會話 ID
   * @param sessionId 會話 ID
   */
  saveSessionId: (sessionId: SessionId): void => {
    secureCookies.setAuth(COOKIE_KEYS.SESSION_ID, sessionId);
  },

  /**
   * 獲取會話 ID
   * @returns 會話 ID 或 null
   */
  retrieveSessionId: (): SessionId | null => {
    return secureCookies.get(COOKIE_KEYS.SESSION_ID) as SessionId | null;
  },

  /**
   * 檢查用戶是否有有效會話
   * @returns 是否有有效會話
   */
  hasValidSession: (): boolean => {
    return secureCookies.has(COOKIE_KEYS.AUTH_TOKEN);
  },

  /**
   * 終止用戶會話
   * 清除所有身份驗證相關的 Cookie
   */
  terminateSession: (): void => {
    secureCookies.clearAuth(AUTH_COOKIE_KEYS);
    // 在這裡可以發布領域事件，例如：
    // domainEvents.publish(new UserLoggedOutEvent());
  },

  /**
   * 創建用戶會話
   * @param sessionData 會話資料
   */
  createSession: (sessionData: SessionData): void => {
    const {
      authToken,
      refreshToken,
      userId,
      sessionId,
      tokenExpiryHours = DEFAULT_TOKEN_EXPIRY_HOURS,
    } = sessionData;

    // 設置身份驗證相關的所有 Cookie
    authRepository.saveToken(authToken, { expiryHours: tokenExpiryHours });

    if (refreshToken) {
      authRepository.saveRefreshToken(refreshToken);
    }

    authRepository.saveUserId(userId);

    if (sessionId) {
      authRepository.saveSessionId(sessionId);
    }

    // 在這裡可以發布領域事件，例如：
    // domainEvents.publish(new UserLoggedInEvent(userId));
  },

  /**
   * 獲取完整的用戶會話資料
   * @returns 會話資料或 null（如果未登入）
   */
  retrieveSession: () => {
    if (!authRepository.hasValidSession()) {
      return null;
    }

    return {
      authToken: authRepository.retrieveToken(),
      refreshToken: authRepository.retrieveRefreshToken(),
      userId: authRepository.retrieveUserId(),
      sessionId: authRepository.retrieveSessionId(),
    };
  },
};
